import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Label, Modal, ModalBody } from "reactstrap";
import { GET_COMPANY_FROM_URL } from "../../../components/Common/CommonFunction";
import { CashierPasswordVerify_Api } from "../../../helpers/backend_helper";

const getInitials = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

const getApiMessage = (msg) => {
    if (!msg) return "";
    if (typeof msg === "string") return msg;
    if (Array.isArray(msg)) return msg.map(getApiMessage).filter(Boolean).join(", ");
    if (typeof msg === "object") return Object.values(msg).map(getApiMessage).filter(Boolean).join(", ");
    return String(msg);
};

const extractApiError = (x) =>
    getApiMessage(
        x?.response?.data?.Message ??
        x?.response?.data?.message ??
        x?.Message ??
        x?.message ??
        (typeof x === "string" ? x : "")
    );

/**
 * Reusable cashier password verification popup.
 *  - cashier    : selected option ({ value, label, UserName? })
 *  - onVerified : called after the login API returns success
 */
const CashierVerifyModal = ({ isOpen, toggle, cashier, onVerified }) => {
    const passwordRef = useRef(null);
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // fresh state every time the popup opens
    useEffect(() => {
        if (isOpen) {
            setPassword("");
            setShowPassword(false);
            setError("");
            setLoading(false);
        }
    }, [isOpen]);

    const closeHandler = () => {
        if (loading) return;
        toggle();
    };

    const verifyHandler = async (event) => {
        event?.preventDefault();
        if (loading) return;

        if (password === "") {
            setError("Please enter Password");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const Company = GET_COMPANY_FROM_URL();
            const values = {
                LoginName: cashier?.UserName || cashier?.label,
                password: password,
                CompanyId: localStorage.getItem(`${Company}_companyID`),
            };

            const resp = await CashierPasswordVerify_Api(values);
            const data = resp?.data ?? resp;

            const isSuccess =
                data?.Status === true &&
                data?.StatusCode === 200 &&
                data?.IsLoginPermissions !== false;

            if (isSuccess) {
                setLoading(false);
                onVerified?.();
            } else {
                setError(extractApiError(data));
            }
        } catch (e) {
            setError(extractApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={closeHandler}
            centered
            size="sm"
            backdrop="static"
            onOpened={() => passwordRef.current?.focus()}
        >
            <ModalBody className="p-4">
                <div className="text-center mb-4">
                    <div
                        className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold"
                        style={{ width: "64px", height: "64px", fontSize: "22px" }}
                    >
                        {getInitials(cashier?.label)}
                    </div>
                    <h5 className="mb-1">{cashier?.label}</h5>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                        <i className="mdi mdi-lock-outline me-1"></i>
                        Enter your password to continue
                    </p>
                </div>

                {error ? (
                    <Alert color="danger" className="py-2 mb-3 text-center" style={{ fontSize: "13px" }}>
                        {error}
                    </Alert>
                ) : null}

                <form onSubmit={verifyHandler}>
                    <div className="mb-4">
                        <Label className="form-label">Password</Label>
                        <div className="input-group">
                            <Input
                                innerRef={passwordRef}
                                name="VerifyPassword"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                autoComplete="off"
                                placeholder="Enter Password"
                                disabled={loading}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (error) setError("");
                                }}
                            />
                            <button
                                className="btn btn-outline-primary"
                                style={{ borderColor: "#d4d4d4" }}
                                type="button"
                                tabIndex={-1}
                                onClick={() => setShowPassword((v) => !v)}
                            >
                                <i className={showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"}></i>
                            </button>
                        </div>
                    </div>

                    <div className="d-flex gap-2">
                        <Button type="button" color="light" className="w-50" onClick={closeHandler} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" color="primary" className="w-50" disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Verifying
                                </>
                            ) : (
                                "Verify"
                            )}
                        </Button>
                    </div>
                </form>
            </ModalBody>
        </Modal>
    );
};

export default CashierVerifyModal;